/**
 * AppICEMAP — ConfirmationBar Component
 * Three vote buttons for report confirmation/denial/resolution.
 *
 * Tag: <confirmation-bar>
 *
 * Properties:
 *   .report     — the report object
 *   .userVote   — 'confirm' | 'deny' | 'resolve' | null
 *   .disabled   — boolean
 *   .isOwnReport — boolean
 *
 * Dispatches:
 *   confirmation-bar:vote → { type: 'confirm' | 'deny' | 'resolve' }
 */

export class ConfirmationBar extends HTMLElement {
	constructor() {
		super();
		this._report = null;
		this._userVote = null;
		this._disabled = false;
		this._isOwnReport = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get report() {
		return this._report;
	}
	set report(val) {
		this._report = val;
		this._renderContent();
	}

	get userVote() {
		return this._userVote;
	}
	set userVote(val) {
		this._userVote = val || null;
		this._updateActiveVote();
	}

	get disabled() {
		return this._disabled;
	}
	set disabled(val) {
		this._disabled = !!val;
		if (this._btns) {
			for (const btn of this._btns) {
				btn.disabled = this._disabled;
			}
		}
		const bar = this.querySelector(".confirmation-bar");
		if (bar) bar.classList.toggle("confirmation-bar--disabled", this._disabled);
	}

	get isOwnReport() {
		return this._isOwnReport;
	}
	set isOwnReport(val) {
		this._isOwnReport = !!val;
		this.disabled = this._disabled || this._isOwnReport;
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="confirmation-bar" role="group" aria-label="Confirmar estado del reporte">
        <button class="vote-btn vote-btn--confirm" data-vote="confirm"
                aria-label="Confirmar: sigue activo">
          <i class="ph ph-thumbs-up" aria-hidden="true"></i>
          <span>Sigue activo</span>
          <span class="vote-count">0</span>
        </button>
        <button class="vote-btn vote-btn--deny" data-vote="deny"
                aria-label="Negar: ya no está">
          <i class="ph ph-thumbs-down" aria-hidden="true"></i>
          <span>Ya no está</span>
          <span class="vote-count">0</span>
        </button>
        <button class="vote-btn vote-btn--resolve" data-vote="resolve"
                aria-label="Marcar como resuelto">
          <i class="ph ph-check-circle" aria-hidden="true"></i>
          <span>Resuelto</span>
        </button>
      </div>
    `;

		this._btns = this.querySelectorAll(".vote-btn");
		this._renderContent();
	}

	_renderContent() {
		if (!this._report || !this._btns) return;

		const confirms = this._report.confirmation_count || 0;
		const denials = this._report.denial_count || 0;

		// Update counts
		const confirmBtn = this.querySelector(".vote-btn--confirm .vote-count");
		const denyBtn = this.querySelector(".vote-btn--deny .vote-count");
		if (confirmBtn) confirmBtn.textContent = String(confirms);
		if (denyBtn) denyBtn.textContent = String(denials);

		// Disable if own report or expired
		const isExpired =
			this._report.status === "expired" || this._report.status === "expiring";
		this.disabled = isExpired || this._isOwnReport;
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		if (!this._btns) return;

		for (const btn of this._btns) {
			btn.addEventListener("click", () => {
				if (this._disabled || this._isOwnReport || this._userVote) return;

				const voteType = btn.dataset.vote;

				// Optimistic update
				this._userVote = voteType;
				this._updateActiveVote();

				// Pulse animation
				btn.classList.add("vote-btn--just-voted");
				setTimeout(() => btn.classList.remove("vote-btn--just-voted"), 300);

				this.dispatchEvent(
					new CustomEvent("confirmation-bar:vote", {
						detail: { type: voteType },
						bubbles: true,
					}),
				);
			});
		}
	}

	_updateActiveVote() {
		if (!this._btns) return;

		for (const btn of this._btns) {
			const isActive = btn.dataset.vote === this._userVote;
			btn.classList.toggle("vote-btn--active", isActive);

			// Switch icon to Fill weight when active
			const icon = btn.querySelector("i");
			if (icon) {
				if (isActive) {
					icon.classList.add("ph-fill");
				} else {
					icon.classList.remove("ph-fill");
				}
			}

			// Disable all if user already voted
			btn.disabled = this._disabled || !!this._userVote;
		}
	}
}

customElements.define("confirmation-bar", ConfirmationBar);
